import { useRef, useEffect, useState } from 'react';
import { MapPin, Image as ImageIcon, BarChart3, Clock, Play, Pause, PersonStanding, MessageSquare, Check, RefreshCw, Trash2 } from 'lucide-react';
import { Stage, Layer, Rect, Circle, Line, Text } from 'react-konva';
import { machineApi, bodyweightApi } from '@/services/api';
import { useUIStore } from '@/store/uiStore';
import { ExerciseStatsPanel, ImageViewer } from '@/components/common';
import type { ExerciseStatsData } from '@/components/common/ExerciseStatsPanel';
import type { Machine, BodyweightExercise, GymFloorPlan } from '@/types';

interface ExerciseTimerProps {
  isActive: boolean;
  elapsedSeconds: number;
  onStart: () => void;
  onStop: () => void;
}

interface ExerciseNotesProps {
  exerciseId: string;
  notes: string | null;
  onNotesUpdate: (exerciseId: string, notes: string | null) => void;
  readOnly?: boolean;
}

interface MachineCardProps {
  machine?: Machine | null;
  bodyweightExercise?: BodyweightExercise | null;
  floorPlan?: GymFloorPlan | null;
  onClick?: () => void;
  exerciseNumber?: number;
  exerciseTimer?: ExerciseTimerProps;
  /** Read-only elapsed time display (for workout history view) */
  elapsedSeconds?: number;
  /** Exercise notes props */
  exerciseNotes?: ExerciseNotesProps;
  /** Callback for swapping exercise (shows swap button next to name) */
  onSwapExercise?: () => void;
  /** Callback for deleting exercise */
  onDeleteExercise?: () => void;
}

export const MachineCard = ({ machine, bodyweightExercise, floorPlan, onClick, exerciseNumber, exerciseTimer, elapsedSeconds, exerciseNotes, onSwapExercise, onDeleteExercise }: MachineCardProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [miniMapSize, setMiniMapSize] = useState({ width: 200, height: 150 });
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState<ExerciseStatsData | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showNotesEditor, setShowNotesEditor] = useState(!!(exerciseNotes?.readOnly && exerciseNotes?.notes));
  const [notesText, setNotesText] = useState(exerciseNotes?.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const notesTextareaRef = useRef<HTMLTextAreaElement>(null);
  const { darkMode } = useUIStore();

  // Determine if this is a bodyweight exercise
  const isBodyweight = !!bodyweightExercise;
  
  // Common display values
  const displayName = bodyweightExercise?.name || machine?.name || '';
  const displayType = bodyweightExercise?.muscleGroup || machine?.type || '';
  const displayPhotoUrl = bodyweightExercise?.photoUrl || machine?.photoUrl || null;
  const displayThumbnailUrl = bodyweightExercise?.thumbnailUrl || machine?.thumbnailUrl || null;
  const displayLocation = machine?.location || null;
  const displayFloorPlanPosition = machine?.floorPlanPosition || null;

  // Update mini map size based on container
  useEffect(() => {
    const updateSize = () => {
      if (mapContainerRef.current) {
        const width = mapContainerRef.current.clientWidth;
        const height = mapContainerRef.current.clientHeight;
        setMiniMapSize({ width: Math.max(width, 100), height: Math.max(height, 100) });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const loadStats = async () => {
    if (stats) {
      setShowStats(!showStats);
      return;
    }

    const exerciseId = bodyweightExercise?.id || machine?.id;
    if (!exerciseId) return;

    setLoadingStats(true);
    try {
      const response = isBodyweight 
        ? await bodyweightApi.getStats(exerciseId, 20)
        : await machineApi.getStats(exerciseId, 20);
      if (response.success && response.data) {
        setStats(response.data);
        setShowStats(true);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Sync notes text when prop changes
  useEffect(() => {
    if (!showNotesEditor) {
      setNotesText(exerciseNotes?.notes || '');
    }
  }, [exerciseNotes?.notes, showNotesEditor]);

  // Focus textarea when opening notes editor
  useEffect(() => {
    if (showNotesEditor && notesTextareaRef.current) {
      notesTextareaRef.current.focus();
    }
  }, [showNotesEditor]);

  const handleNotesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowNotesEditor(!showNotesEditor);
  };

  const handleNotesSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!exerciseNotes) return;

    setSavingNotes(true);
    try {
      const trimmedNotes = notesText.trim();
      await exerciseNotes.onNotesUpdate(exerciseNotes.exerciseId, trimmedNotes || null);
      setShowNotesEditor(false);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleNotesClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNotesText(exerciseNotes?.notes || '');
    setShowNotesEditor(false);
  };

  const handleStatsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    loadStats();
  };

  const handleCloseStats = () => {
    setShowStats(false);
  };

  const miniMapWidth = miniMapSize.width;
  const miniMapHeight = miniMapSize.height;

  const renderMiniMap = () => {
    if (!floorPlan || !displayFloorPlanPosition) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-400">
          <div className="text-center">
            {isBodyweight ? (
              <>
                <PersonStanding className="w-8 h-8 mx-auto mb-2" />
                <p className="text-xs">Testsúlyos</p>
              </>
            ) : (
              <>
                <MapPin className="w-8 h-8 mx-auto mb-2" />
                <p className="text-xs">Nincs térkép</p>
              </>
            )}
          </div>
        </div>
      );
    }

    // Minimal padding (2px on each side)
    const padding = 2;
    const availableWidth = miniMapWidth - (padding * 2);
    const availableHeight = miniMapHeight - (padding * 2);

    // Scale to fit mini map with padding
    const scaleX = availableWidth / floorPlan.drawingData.width;
    const scaleY = availableHeight / floorPlan.drawingData.height;
    const scale = Math.min(scaleX, scaleY);

    // Scaled dimensions
    const scaledWidth = floorPlan.drawingData.width * scale;
    const scaledHeight = floorPlan.drawingData.height * scale;

    // Center offset (including padding)
    const offsetX = (miniMapWidth - scaledWidth) / 2;
    const offsetY = (miniMapHeight - scaledHeight) / 2;

    // Machine marker position
    const markerX = displayFloorPlanPosition.x * scale + offsetX;
    const markerY = displayFloorPlanPosition.y * scale + offsetY;

    return (
      <Stage 
        width={miniMapWidth} 
        height={miniMapHeight}
        listening={false}
        style={{ display: 'block', touchAction: 'auto' }}
      >
        <Layer>
          {/* Background */}
          <Rect
            x={0}
            y={0}
            width={miniMapWidth}
            height={miniMapHeight}
            fill={darkMode ? '#1f2937' : '#f9fafb'}
          />

          {/* Shapes from floor plan */}
          {floorPlan.drawingData.shapes.map((shape) => {
            const shapeX = shape.x * scale + offsetX;
            const shapeY = shape.y * scale + offsetY;

            switch (shape.type) {
              case 'rect':
                return (
                  <Rect
                    key={shape.id}
                    listening={false}
                    x={shapeX}
                    y={shapeY}
                    width={(shape.width || 0) * scale}
                    height={(shape.height || 0) * scale}
                    fill={shape.fill}
                    stroke={shape.stroke}
                    strokeWidth={1}
                  />
                );
              case 'circle':
                return (
                  <Circle
                    key={shape.id}
                    listening={false}
                    x={shapeX}
                    y={shapeY}
                    radius={(shape.radius || 0) * scale}
                    fill={shape.fill}
                    stroke={shape.stroke}
                    strokeWidth={1}
                  />
                );
              case 'line':
                return (
                  <Line
                    key={shape.id}
                    listening={false}
                    points={(shape.points || []).map((p, i) => 
                      i % 2 === 0 ? p * scale + offsetX : p * scale + offsetY
                    )}
                    stroke={shape.stroke}
                    strokeWidth={1.5}
                  />
                );
              case 'text':
                return (
                  <Text
                    key={shape.id}
                    listening={false}
                    x={shapeX}
                    y={shapeY}
                    text={shape.text}
                    fontSize={Math.max(6, 10 * scale)}
                    fill={darkMode ? '#ffffff' : (shape.fill || '#000000')}
                  />
                );
              default:
                return null;
            }
          })}

          {/* Machine marker */}
          <Circle
            x={markerX}
            y={markerY}
            radius={7}
            fill="#3b82f6"
            opacity={0.3}
            listening={false}
          />
          <Circle
            x={markerX}
            y={markerY}
            radius={3.5}
            fill="#3b82f6"
            stroke="#ffffff"
            strokeWidth={1.5}
            listening={false}
          />
        </Layer>
      </Stage>
    );
  };

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      className={`w-full border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden ${onClick ? 'hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-all cursor-pointer' : ''}`}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      {/* Top: Machine Info */}
      <div className={`p-2 border-b border-gray-200 dark:border-gray-700 ${exerciseTimer?.isActive ? 'bg-primary-50 dark:bg-primary-900/30' : 'bg-white dark:bg-gray-800'}`}>
        {/* Exercise timer row - only when exerciseTimer prop is provided */}
        {exerciseTimer && (
          <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200 dark:border-gray-600">
            <div className="flex items-center gap-2">
              {exerciseTimer.isActive && (
                <span className="flex h-2 w-2 relative">
                  <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-primary-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
                </span>
              )}
              <span className={`text-sm font-medium ${exerciseTimer.isActive ? 'text-primary-700 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400'}`}>
                {exerciseTimer.isActive ? 'Aktív gyakorlat' : 'Befejezett'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* Notes icon - to the left of timer */}
              {exerciseNotes && (
                <button
                  type="button"
                  onClick={handleNotesClick}
                  className={`p-1.5 rounded-lg transition-colors ${
                    exerciseNotes.notes
                      ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400'
                      : showNotesEditor
                        ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400'
                  }`}
                  title={exerciseNotes.notes ? 'Megjegyzés szerkesztése' : 'Megjegyzés hozzáadása'}
                >
                  <MessageSquare className="w-4 h-4" />
                </button>
              )}
              <div className="flex items-center gap-1.5">
                <Clock className={`w-4 h-4 ${exerciseTimer.isActive ? 'text-primary-600' : 'text-gray-400'}`} />
                <span className={`font-mono text-sm font-semibold ${exerciseTimer.isActive ? 'text-primary-600' : 'text-gray-500'}`}>
                  {(() => {
                    const minutes = Math.floor(exerciseTimer.elapsedSeconds / 60);
                    const seconds = exerciseTimer.elapsedSeconds % 60;
                    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                  })()}
                </span>
              </div>
              {exerciseTimer.isActive ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); exerciseTimer.onStop(); }}
                  className="p-1.5 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors"
                  title="Gyakorlat befejezése"
                >
                  <Pause className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); exerciseTimer.onStart(); }}
                  className="p-1.5 bg-primary-100 hover:bg-primary-200 text-primary-600 rounded-lg transition-colors"
                  title="Gyakorlat folytatása"
                >
                  <Play className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}
        {/* Read-only elapsed time display - for workout history view */}
        {elapsedSeconds !== undefined && !exerciseTimer && (
          <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Gyakorlat időtartama
            </span>
            <div className="flex items-center gap-2">
              {/* Notes icon in history view */}
              {exerciseNotes && (
                <button
                  type="button"
                  onClick={handleNotesClick}
                  className={`p-1.5 rounded-lg transition-colors ${
                    exerciseNotes.notes
                      ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400'
                      : showNotesEditor
                        ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400'
                  }`}
                  title={exerciseNotes.notes ? 'Megjegyzés szerkesztése' : 'Megjegyzés hozzáadása'}
                >
                  <MessageSquare className="w-4 h-4" />
                </button>
              )}
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="font-mono text-sm font-semibold text-gray-600 dark:text-gray-300">
                  {(() => {
                    const minutes = Math.floor(elapsedSeconds / 60);
                    const seconds = elapsedSeconds % 60;
                    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                  })()}
                </span>
              </div>
            </div>
          </div>
        )}
        {/* Notes editor panel */}
        {showNotesEditor && exerciseNotes && (
          <div className="mb-2 pb-2 border-b border-gray-200 dark:border-gray-600" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1.5">
              <MessageSquare className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Megjegyzés</span>
            </div>
            {exerciseNotes.readOnly ? (
              <div className="text-sm text-gray-700 dark:text-gray-300 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2 border border-amber-200 dark:border-amber-800">
                {exerciseNotes.notes}
              </div>
            ) : (
              <>
                <textarea
                  ref={notesTextareaRef}
                  value={notesText}
                  onChange={(e) => setNotesText(e.target.value)}
                  placeholder="Írj megjegyzést ehhez a gyakorlathoz..."
                  className="w-full text-sm p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  rows={2}
                  maxLength={1000}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[10px] text-gray-400">{notesText.length}/1000</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={handleNotesClose}
                      className="px-2 py-1 text-xs rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 transition-colors"
                    >
                      Mégse
                    </button>
                    <button
                      type="button"
                      onClick={handleNotesSave}
                      disabled={savingNotes}
                      className="px-2.5 py-1 text-xs rounded-md bg-primary-600 hover:bg-primary-700 text-white font-medium transition-colors disabled:opacity-50 flex items-center gap-1"
                    >
                      {savingNotes ? (
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Check className="w-3 h-3" />
                      )}
                      Mentés
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        {/* Existing notes indicator when editor is closed */}
        {!showNotesEditor && exerciseNotes?.notes && exerciseTimer && (
          <button
            type="button"
            onClick={handleNotesClick}
            className="flex items-center gap-1.5 mb-2 pb-2 border-b border-gray-200 dark:border-gray-600 w-full text-left"
          >
            <MessageSquare className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
            <span className="text-xs text-amber-700 dark:text-amber-400 truncate">{exerciseNotes.notes}</span>
          </button>
        )}
        {!showNotesEditor && exerciseNotes?.notes && !exerciseTimer && elapsedSeconds === undefined && (
          <button
            type="button"
            onClick={handleNotesClick}
            className="flex items-center gap-1.5 mb-2 pb-2 border-b border-gray-200 dark:border-gray-600 w-full text-left"
          >
            <MessageSquare className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
            <span className="text-xs text-amber-700 dark:text-amber-400 truncate">{exerciseNotes.notes}</span>
          </button>
        )}
        <div className="flex items-start gap-3">
          {exerciseNumber && (
            <div className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 bg-primary-600 text-white font-bold rounded-full text-base">
              {exerciseNumber}
            </div>
          )}
          <div className="flex-1 text-left">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 dark:text-gray-100">{displayName}</span>
              {onSwapExercise && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onSwapExercise(); }}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 bg-gray-100 dark:bg-gray-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 border border-gray-200 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-500 rounded-md transition-all"
                  title="Gyakorlat cseréje"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span>Csere</span>
                </button>
              )}
              {onDeleteExercise && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDeleteExercise(); }}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 bg-gray-100 dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/20 border border-gray-200 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-500 rounded-md transition-all"
                  title="Gyakorlat törlése"
                >
                  <Trash2 className="w-3 h-3" />
                  <span>Törlés</span>
                </button>
              )}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{displayType}</div>
            {floorPlan && <div className="text-xs text-primary-600">{floorPlan.name}</div>}
            {displayLocation && <div className="text-xs text-gray-500 dark:text-gray-400">{displayLocation}</div>}
          </div>
          {/* Notes toggle button (when no timer row and no elapsed time row to hold the icon) */}
          {!exerciseTimer && elapsedSeconds === undefined && exerciseNotes && !exerciseNotes.readOnly && (
            <button
              type="button"
              onClick={handleNotesClick}
              className={`p-1.5 rounded-lg transition-colors ${
                exerciseNotes.notes
                  ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400'
                  : showNotesEditor
                    ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400'
              }`}
              title={exerciseNotes.notes ? 'Megjegyzés szerkesztése' : 'Megjegyzés hozzáadása'}
            >
              <MessageSquare className="w-4 h-4" />
            </button>
          )}
          {/* Stats toggle button */}
          <button
            type="button"
            onClick={handleStatsClick}
            className={`p-1.5 rounded-lg transition-colors ${showStats ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400'}`}
            title="Statisztika"
          >
            {loadingStats ? (
              <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            ) : (
              <BarChart3 className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Stats Panel - Between header and photo/map */}
      {showStats && stats && (
        <div className="border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-primary-50 to-white dark:from-gray-700 dark:to-gray-800 p-3">
          <ExerciseStatsPanel
            stats={stats}
            onClose={handleCloseStats}
            stopPropagation
          />
        </div>
      )}

      {/* Bottom: Photo (left) and Floor Plan (right) */}
      <div className="flex border-t border-gray-200 dark:border-gray-700">
        {/* Left: Machine Photo */}
        <div className="w-1/2 bg-gray-100 dark:bg-gray-700 flex items-center justify-center border-r border-gray-200 dark:border-gray-700">
          {displayPhotoUrl ? (
            <img
              src={displayThumbnailUrl || displayPhotoUrl}
              alt={displayName}
              className="w-full h-48 object-cover cursor-pointer"
              onClick={(e) => { e.stopPropagation(); setShowImageViewer(true); }}
            />
          ) : (
            <div className="text-gray-400 py-16">
              {isBodyweight ? (
                <PersonStanding className="w-12 h-12 mx-auto mb-2" />
              ) : (
                <ImageIcon className="w-12 h-12 mx-auto mb-2" />
              )}
              <p className="text-xs">Nincs kép</p>
            </div>
          )}
        </div>

        {/* Image Viewer Lightbox */}
        {displayPhotoUrl && (
          <ImageViewer
            src={displayPhotoUrl}
            alt={displayName}
            isOpen={showImageViewer}
            onClose={() => setShowImageViewer(false)}
          />
        )}

        {/* Right: Floor Plan Mini Map */}
        <div className="w-1/2">
          <div 
            ref={mapContainerRef}
            className="h-48 flex items-center justify-center bg-gray-50 dark:bg-gray-800"
          >
            {renderMiniMap()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MachineCard;
